import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, CalendarClock, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/components/Analytics";
import SuccessCheck from "@/components/SuccessCheck";

export default function VisitRequestModal({ open, onClose, project }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    visit_type: "on_site",
    preferred_date: "",
    preferred_time: "morning",
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      await api.post("/public/visit-requests", {
        ...form,
        project_id: project?.id || null,
        project_name: project?.name || "",
      });
      setSuccess(true);
      trackEvent("visit_request_submit", {
        project_name: project?.name || "general",
        visit_type: form.visit_type,
      });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Une erreur est survenue.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-atelier-deep/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-auto" onClick={onClose} dir="auto">
      <div className="bg-atelier-surface atelier-border max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="visit-modal">
        <div className="flex items-center justify-between p-6 border-b atelier-border-soft">
          <div>
            <div className="label-tech text-atelier-ink mb-1 flex items-center gap-2"><CalendarClock className="w-3 h-3" /> {project?.name || "—"}</div>
            <h3 className="font-serif text-2xl text-atelier-text">{t("visit.title")}</h3>
          </div>
          <button onClick={onClose} data-testid="visit-modal-close" className="text-atelier-textDim hover:text-atelier-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="visit-success">
            <div className="flex justify-center mb-4"><SuccessCheck size={64} /></div>
            <h4 className="font-serif text-2xl mb-3 text-atelier-text">{t("visit.successTitle")}</h4>
            <p className="text-atelier-textDim mb-6 font-serif">{t("visit.successDesc")}</p>
            <button onClick={onClose} className="btn-outline">OK</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 lg:p-8 space-y-4">
            <p className="text-sm text-atelier-textDim font-serif">{t("visit.subtitle")}</p>

            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "on_site", label: t("visit.typeOnSite") },
                { v: "virtual", label: t("visit.typeVirtual") },
                { v: "call_back", label: t("visit.typeCallback") },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  data-testid={`visit-type-${opt.v}`}
                  onClick={() => setForm({ ...form, visit_type: opt.v })}
                  className={`px-3 py-3 label-tech text-[0.6rem] border transition-all duration-300 ${
                    form.visit_type === opt.v
                      ? "bg-atelier-ink text-atelier-deep border-atelier-ink"
                      : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("contact.firstName")} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testId="visit-first-name" />
              <Field label={t("contact.lastName")} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testId="visit-last-name" />
              <Field label={t("contact.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="visit-email" />
              <Field label={t("contact.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testId="visit-phone" />
              <Field label={t("visit.date")} type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testId="visit-date" />
              <div>
                <label className="label-tech text-atelier-textFaint mb-2 block">{t("visit.time")}</label>
                <select value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="atelier-select w-full" data-testid="visit-time">
                  <option value="morning">{t("visit.morning")}</option>
                  <option value="afternoon">{t("visit.afternoon")}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="label-tech text-atelier-textFaint mb-2 block">{t("visit.notes")}</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="atelier-input w-full"
                data-testid="visit-notes"
              />
            </div>

            {err && <div className="text-sm text-red-400 font-serif">{err}</div>}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-outline">{t("cta.cancel")}</button>
              <button type="submit" disabled={sending} className="btn-primary" data-testid="visit-submit">
                {sending ? t("cta.sending") : t("visit.submit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="label-tech text-atelier-textFaint mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="atelier-input w-full"
      />
    </div>
  );
}
